'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronRight, Lightbulb, Info, AlertTriangle, Search } from 'lucide-react';
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerContent,
  DrawerClose,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useHelp } from '@/contexts/help-context';
import { getHelpTopic, searchHelpTopics, type HelpBlock, type HelpTopic } from '@/lib/help/content';

/**
 * Renders a single help content block. Kept generic so help content authors
 * never have to write JSX — see {@link HelpBlock}.
 */
function HelpBlockView({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'heading':
      return <h3 className="text-base font-semibold text-foreground mt-2">{block.text}</h3>;
    case 'paragraph':
      return <p className="text-sm text-muted-foreground leading-relaxed">{block.text}</p>;
    case 'list':
      return (
        <ul className="space-y-1.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm text-muted-foreground">
              <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0 text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm text-muted-foreground">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {i + 1}
              </span>
              <span className="mt-px">{item}</span>
            </li>
          ))}
        </ol>
      );
    case 'callout': {
      const styles = {
        tip: {
          wrap: 'border-green-500/30 bg-green-500/10',
          icon: 'text-green-500',
          Icon: Lightbulb,
        },
        info: { wrap: 'border-blue-500/30 bg-blue-500/10', icon: 'text-blue-500', Icon: Info },
        warning: {
          wrap: 'border-yellow-500/30 bg-yellow-500/10',
          icon: 'text-yellow-500',
          Icon: AlertTriangle,
        },
      }[block.variant];
      const Icon = styles.Icon;
      return (
        <div className={cn('rounded-lg border p-3 flex gap-3', styles.wrap)}>
          <Icon className={cn('h-4 w-4 mt-0.5 flex-shrink-0', styles.icon)} />
          <div className="space-y-1">
            {block.title && <p className="text-sm font-semibold text-foreground">{block.title}</p>}
            <p className="text-sm text-muted-foreground leading-relaxed">{block.text}</p>
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}

/** The index/list of all topics (also used as search results). */
function HelpIndex({ topics, onSelect }: { topics: HelpTopic[]; onSelect: (id: string) => void }) {
  if (topics.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        No help topics match your search.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {topics.map((topic) => (
        <button
          key={topic.id}
          type="button"
          onClick={() => onSelect(topic.id)}
          className="w-full text-left rounded-lg border border-border bg-muted/30 p-4 transition-colors hover:bg-muted/60 hover:border-border/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-foreground">{topic.title}</span>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground mt-1">{topic.summary}</p>
        </button>
      ))}
    </div>
  );
}

/** A single topic's full content, with related-topic links. */
function HelpTopicView({ topic, onSelect }: { topic: HelpTopic; onSelect: (id: string) => void }) {
  const related = (topic.related ?? [])
    .map((id) => getHelpTopic(id))
    .filter((t): t is HelpTopic => Boolean(t));

  return (
    <div className="space-y-4">
      {topic.blocks.map((block, i) => (
        <HelpBlockView key={i} block={block} />
      ))}

      {related.length > 0 && (
        <div className="pt-4 mt-2 border-t border-border">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Related
          </p>
          <div className="space-y-1.5">
            {related.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t.id)}
                className="flex w-full items-center gap-2 text-sm text-primary hover:underline focus:outline-none"
              >
                <ChevronRight className="h-4 w-4" />
                {t.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The contextual help side-drawer. Mount once in the dashboard layout. It reads
 * its open/topic state from {@link useHelp}, so any component can open it without
 * navigating away from the current page.
 */
export function HelpDrawer() {
  const { isOpen, activeTopicId, closeHelp, showTopic } = useHelp();
  const [query, setQuery] = useState('');

  const activeTopic = getHelpTopic(activeTopicId);
  const results = useMemo(() => searchHelpTopics(query), [query]);

  // Reset search whenever the drawer closes or a topic is opened so the index is
  // clean the next time the user lands on it.
  useEffect(() => {
    if (!isOpen || activeTopicId) {
      setQuery('');
    }
  }, [isOpen, activeTopicId]);

  // Close on Escape (the base Drawer does not handle keyboard).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeHelp();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeHelp]);

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeHelp()}>
      <DrawerHeader className="relative">
        {activeTopic ? (
          <button
            type="button"
            onClick={() => showTopic(null)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1 w-fit focus:outline-none"
          >
            <ArrowLeft className="h-4 w-4" />
            All topics
          </button>
        ) : null}
        <DrawerTitle>{activeTopic ? activeTopic.title : 'Help & Guides'}</DrawerTitle>
        {!activeTopic && (
          <DrawerDescription>
            Learn how each part of Email Ferret works — without leaving the page.
          </DrawerDescription>
        )}
        <DrawerClose onClick={closeHelp} />
      </DrawerHeader>

      <DrawerContent>
        {activeTopic ? (
          <HelpTopicView topic={activeTopic} onSelect={showTopic} />
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search help…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                aria-label="Search help topics"
              />
            </div>
            <HelpIndex topics={results} onSelect={showTopic} />
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
